import {
  type ComputedRef,
  type MaybeRefOrGetter,
  computed,
  defineAsyncComponent,
  toValue,
  watch,
} from 'vue'
import { ApolloCache, type Reference } from '@apollo/client/cache'
import { useNewProjectModal } from '/@/common/projects/modals/NewProjectModal'
import { useLoading } from '/@/core/elements/loading'
import { useTracker } from '/@/core/tracking/api'
import { useDesignerApi } from '/@/data/designer/api'
import { useCurrentDesigner } from '/@/data/designer/current'
import {
  useProjectApi,
  useProjectData,
  useProjectsListExists,
} from '/@/common/projects/api/index'
import { projectQuery } from '/@/common/projects/api/fragments'
import { type Project, type ProjectInfo } from './types'
import { isProduct, isProjectGenericThing } from '/@/common/things/utils'
import { usePermission } from '/@/common/tiers'
import { stellaModal } from '/@/stella/StellaModal'
import type { ProjectThingType } from '/@/common/things/types'
import type { ViewerType } from '/@/utils/misc'
import { useUser } from '/@/data/user'

export type ProjectGuardTargetType =
  | 'Vizi'
  | 'Thing'
  | 'Color Pal'
  | 'Design Challenge'
  | 'Daily Play'

export interface ProjectGuardVerify {
  projectId: string
  projectName: string
  isNew: boolean
}

export interface ProjectVerifyOptions {
  strict?: boolean
  automaticallyCreate?: boolean
}

const AUTOMATIC_PROJECT_NAME = 'Playing Around'
const DEFAULT_PROJECT_NAME = 'Untitled Project'

export function useProjectGuard() {
  const newProjectModal = useNewProjectModal()
  const designerApi = useDesignerApi()
  const projectApi = useProjectApi()
  const loadingApi = useLoading()
  const tracker = useTracker()
  const createProjectPermission = usePermission('CREATE_PROJECTS')

  const { designer, loading: designerLoading } = useCurrentDesigner()
  const { hasProjects, loading: projectsLoading } = useProjectsListExists(
    computed(() => ({})),
  )
  const automaticProject = computed(() => designer.value?.automaticProject)
  const loading = computed(
    () =>
      designerLoading.value ||
      projectsLoading.value ||
      createProjectPermission.loading.value,
  )

  async function switchProject(id: string) {
    await loadingApi.waitTask(() => {
      if (!designer.value) return

      tracker.send('Project Selection Confirmed', {
        sameProject: designer.value.currentProject?.id === id,
      })

      return designerApi.updateCurrentProject(id)
    })
  }

  function verifyImpl(
    resolve: (
      value: ProjectGuardVerify | PromiseLike<ProjectGuardVerify>,
    ) => void,
    reject: (reason?: any) => void,
    targetName?: ProjectGuardTargetType,
    options?: ProjectVerifyOptions,
  ) {
    if (targetName === 'Daily Play' || targetName === 'Design Challenge') {
      if (automaticProject.value) {
        resolve({
          projectId: automaticProject.value.id,
          projectName: automaticProject.value.name,
          isNew: false,
        })
      } else {
        loadingApi.waitTask(
          async () =>
            await projectApi
              .create({
                name: AUTOMATIC_PROJECT_NAME,
                visibility: 'PRIVATE',
                projectType: 'AUTOMATIC_DAILY_PLAY_DESIGN_CHALLENGE',
              })
              .then((project: Project | undefined) => {
                if (project)
                  resolve({
                    projectId: project.id,
                    projectName: project.name,
                    isNew: true,
                  })
                else reject('Failed to create project')
              }),
        )
      }
    } else {
      const currentProject = designer.value?.currentProject?.access?.canEdit
        ? designer.value?.currentProject
        : null
      if (currentProject) {
        resolve({
          projectId: currentProject.id,
          projectName: currentProject.name,
          isNew: false,
        })
      } else {
        tracker.send('Project Selection Shown')

        if (
          !createProjectPermission.hasPermission.value &&
          hasProjects.value &&
          (targetName === 'Vizi' || targetName === 'Color Pal')
        ) {
          stellaModal.open({
            content: defineAsyncComponent(
              () =>
                import(
                  '/@/common/projects/modals/SelectProjectModal/SelectProjectModalContent.vue'
                ),
            ),
            data: {
              onSubmit: async (project: { id: string; name: string }) => {
                await switchProject(project.id)
                resolve({
                  projectId: project.id,
                  projectName: project.name,
                  isNew: false,
                })
              },
            },
          })
        } else if (
          createProjectPermission.hasPermission.value &&
          options?.automaticallyCreate
        ) {
          loadingApi.waitTask(async () => {
            await projectApi
              .create({
                name: DEFAULT_PROJECT_NAME,
                visibility: 'PUBLIC',
              })
              .then((project: Project | undefined) => {
                if (project)
                  resolve({
                    projectId: project.id,
                    projectName: project.name,
                    isNew: true,
                  })
                else reject('Failed to create project')
              })
          })
        } else {
          createProjectPermission
            .tierGuard(
              () =>
                newProjectModal.show({
                  context: targetName,
                  onSubmit: async (project, isNew) => {
                    await switchProject(project.id)
                    resolve({
                      projectId: project.id,
                      projectName: project.name,
                      isNew,
                    })
                  },
                  onCancel: reject,
                  strict: options?.strict ?? false,
                }),
              {
                source: 'Project Guard',
              },
            )
            .catch(reject)
        }
      }
    }
  }

  async function verify(
    targetName?: ProjectGuardTargetType,
    options?: ProjectVerifyOptions,
  ): Promise<ProjectGuardVerify> {
    // Cannot use `unwatch` if `watch` is called with `immediate: true`, so check values first before using `watch`
    if (!loading.value) {
      return new Promise((resolve, reject) => {
        verifyImpl(resolve, reject, targetName, options)
      })
    } else {
      return new Promise((resolve, reject) => {
        const unwatch = watch(
          loading,
          l => {
            if (l) return
            verifyImpl(resolve, reject, targetName, options)
            unwatch()
          },
          {
            once: true,
          },
        )
      })
    }
  }

  return {
    loading,
    verify,
  }
}

export function useProjectCache() {
  function getCachedProject(projectId: ComputedRef<string>) {
    return useProjectData(projectId, 'cache-only')
  }

  function getApolloCachedProject(
    cache: ApolloCache<unknown>,
    projectId: string,
  ) {
    return cache.readQuery<{ project: Project }>({
      query: projectQuery,
      variables: {
        projectId,
      },
    })?.project
  }

  function addProjectBudget(
    cache: ApolloCache<unknown>,
    budget: Reference,
    projectId: string,
  ) {
    const cachedProject = getApolloCachedProject(cache, projectId)
    if (!cachedProject) return
    const id = cache.identify({ ...cachedProject })
    if (!id) return
    cache.modify({
      id,
      fields: {
        ['budget']() {
          return budget
        },
      },
    })
  }

  function addSectionBudget(
    cache: ApolloCache<unknown>,
    budget: Reference,
    projectId: string,
    sectionId: string,
  ) {
    const cachedProject = getApolloCachedProject(cache, projectId)
    if (!cachedProject) return
    const cachedSection = cachedProject.sections.find(s => s.id === sectionId)
    if (!cachedSection) return
    const id = cache.identify({ ...cachedSection })
    if (!id) return
    cache.modify({
      id,
      fields: {
        ['budget']() {
          return budget
        },
      },
    })
  }

  function deleteProjectBudget(cache: ApolloCache<unknown>, projectId: string) {
    const cachedProject = getApolloCachedProject(cache, projectId)
    if (!cachedProject) return
    const id = cache.identify({ ...cachedProject })
    if (!id) return
    cache.modify({
      id,
      fields: {
        ['budget']() {
          return null
        },
      },
    })
  }

  function deleteSectionBudget(
    cache: ApolloCache<unknown>,
    projectId: string,
    sectionId: string,
  ) {
    const cachedProject = getApolloCachedProject(cache, projectId)
    if (!cachedProject) return
    const cachedSection = cachedProject.sections.find(s => s.id === sectionId)
    if (!cachedSection) return
    const id = cache.identify({ ...cachedSection })
    if (!id) return
    cache.modify({
      id,
      fields: {
        ['budget']() {
          return null
        },
      },
    })
  }

  return {
    getCachedProject,
    addProjectBudget,
    deleteProjectBudget,
    addSectionBudget,
    deleteSectionBudget,
  }
}

export function useProjectThingCache() {
  function getCachedProjectThing(
    cache: ApolloCache<unknown>,
    projectId: string,
    projectThingId: string,
    projectThingType: ProjectThingType,
  ) {
    const project = cache.readQuery<{ project: Project }>({
      query: projectQuery,
      variables: {
        projectId,
      },
    })?.project
    const projectThing = project?.things.find(t => {
      return t.id === projectThingId && t.thingType === projectThingType
    })
    return projectThing &&
      (isProduct(projectThing) || isProjectGenericThing(projectThing))
      ? projectThing
      : null
  }

  function addProjectThingBudget(
    cache: ApolloCache<unknown>,
    budget: Reference,
    projectId: string,
    projectThingId: string,
    projectThingType: ProjectThingType,
  ) {
    const cachedProjectThing = getCachedProjectThing(
      cache,
      projectId,
      projectThingId,
      projectThingType,
    )
    if (!cachedProjectThing) return
    const id = cache.identify({ ...cachedProjectThing })
    if (!id) return
    cache.modify({
      id,
      fields: {
        ['thingBudget']() {
          return budget
        },
      },
    })
  }

  function deleteProjectThingBudget(
    cache: ApolloCache<unknown>,
    projectId: string,
    projectThingId: string,
    projectThingType: ProjectThingType,
  ) {
    const cachedProjectThing = getCachedProjectThing(
      cache,
      projectId,
      projectThingId,
      projectThingType,
    )
    if (!cachedProjectThing) return
    const id = cache.identify({ ...cachedProjectThing })
    if (!id) return
    cache.modify({
      id,
      fields: {
        ['thingBudget']() {
          return null
        },
      },
    })
  }

  return {
    addProjectThingBudget,
    deleteProjectThingBudget,
  }
}

export function useProjectViewer(
  project: MaybeRefOrGetter<ProjectInfo | null | undefined>,
) {
  const p = toValue(project)
  return computed<ViewerType>(() => {
    const user = useUser()
    if (!p) return 'anonymous'
    const role = p.access?.role
    if (role === 'OWNER') return 'owner'
    if (role === 'COLLABORATOR') return 'collaborator'
    if (role === 'CLIENT') return 'client'
    if (user?.current?.designer?.id) return 'designer'
    return 'anonymous'
  })
}

export const projectLibraryIds = [
  '548542',
  '548543',
  '548203',
  '548597',
  '548598',
  '603354',
]
